<template>
  <div
    ref="inputWithDropdown"
    class="InputWithDropdown"
  >
    <div
        class="flex items-center gap-8 px-12 type-sm border border-border-subtle hover:border-border-base relative z-above transition-all"
        :class="[inputHeight]"
        @click="open = true"
    >
      <GlobalIcon
          name="search"
          size="small"
      />
        <!-- input -->
        <input
          v-show="open || selectedOptions.length === 0"
          :id="componentId"
          ref="searchInput"
          v-model="input"
          type="text"
          :placeholder="placeholder"
          class="leading-single basis-full"
          @focus="open = true"
        >

        <!-- selected options -->
        <div
          v-if="!open"
        >
          <div
            v-for="selected in selectedLabels"
            :key="selected.id"
            class="type-sm inline-flex items-center whitespace-nowrap mr-12 leading-single py-2"
          >
            <span class="mr-4">{{ selected.label }}</span>
            <img
              v-if="selected.label"
              alt="close"
              src="/icons/close-circle.svg"
              class="relative h-16 w-16 ml-auto duration-300 cursor-pointer"
              @click.prevent.stop="onDelete(selected.id)"
            >
          </div>
        </div>

      <!-- options dropdown -->
      <div
        v-if="open"
        class="py-8 w-full absolute left-0 flex flex-col items-stretch justify-start type-sm border border-border-subtle shadow-lg z-20 bg-bg-base"
        :class="[optionsDropdown]"
      >
        <div
          v-for="item in filteredItems"
          :key="item.value"
          class="px-12 py-8 hover:bg-bg-subtle cursor-pointer flex items-center content-start"
          :class="{
            'bg-bg-subtle': selectedOptions.includes(item.value),
          }"
          @click="onSelect(item.value)"
        >
          <div class="content-start flex">
            <input
              type="checkbox"
              class="whiteCheck darkestChecked"
              :checked="selectedOptions.includes(item.value)"
            >
          </div>
          <div class="flex items-center cursor-pointer">
            <span class="ml-12 font-semibold">{{ item.label }}</span>

            <span class="text-text-subtle ml-6">
              {{ item.extraText ? item.extraText : `#${item.value}` }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';

const componentId = useId();

type Options = {
  value: string | number;
  label: string,
  disabled?: boolean,
  extraText?: string,
}

const props = defineProps<{
  items: Options[],
  placeholder: string;
  selectedPlaceholder: string; // shows instad of placeholder when something is selected
  defaultValue?: Array<string | number>;
  prefix?: string;
  multiSelect?: boolean;
  design?: 'lg' | 'base' | 'md' | 'sm';
}>();

const emit = defineEmits<{
  (e: 'select', input: Array<string | number>): void;
  (e: 'close'): void;
}>();

const selectedOptions = ref<Array<string | number>>([]);
const open = ref(false);
const input = ref('');
const inputWithDropdown = ref();

const searchInput = ref<HTMLElement>();

watch(open, (newVal) => {
  if (newVal) {
    nextTick(()=> searchInput.value?.focus());
  }
});

const close = () => {
  open.value = false;
  emit('close');
};

onMounted(() => {
  selectedOptions.value = props.defaultValue || [];
});

const selectedLabels = computed(() => {
  //return props.items.filter(item => selectedOptions.value.includes(item.value)).map(item => (item.label));

  return selectedOptions.value.map((i) => {
    return {
      id: i,
      label: props.items.find(f => f.value === i)?.label || '',
    };
  });

});

const filteredItems = computed(() => {
  const regex = new RegExp(input.value, 'i');
  return props.items.filter(item => {
    return regex.test(item.label) || regex.test(item.value.toString());
  });
});

const onSelect = (item: string | number) => {
  // deselect
  if (selectedOptions.value.includes(item)) {
    selectedOptions.value = selectedOptions.value.filter(f => f !== item);
  } else {
    if (props.multiSelect) {
      selectedOptions.value.push(item);
    } else {
      selectedOptions.value = [ item ];
    }
  }

  emit('select', selectedOptions.value);

  if (!props.multiSelect) {
    close();
  }
};

const onDelete = (item: string | number) => {
  selectedOptions.value = selectedOptions.value.filter(f => f !== item);
  emit('select', selectedOptions.value);
};

const inputHeight = computed(() => {
  if (props.design === 'sm') return 'h-32';
  if (props.design === 'md') return 'h-36';
  if (props.design === 'base') return 'h-40';
  return 'h-48'; //
});

const optionsDropdown = computed(() => {
  if (props.design === 'sm') return 'top-32';
  if (props.design === 'md') return 'top-36';
  if (props.design === 'base') return 'top-40';
  return 'top-48';
});


onClickOutside(inputWithDropdown, () => {
  close();
});

watch(() => props.defaultValue, () => {
  if (props.defaultValue) {
    selectedOptions.value = props.defaultValue;
  }
});
</script>
